/**
 * Formats an address string in the format of "123 Main St, Springfield, IL 62704"
 * Returns an empty string if all inputs are null/undefined
 */
export function formatAddress(
  streetAddress: string | null | undefined,
  city: string | null | undefined,
  state: string | null | undefined,
  zip: string | null | undefined,
  country?: string | null | undefined
): string {
  // Trim each part if it exists
  const trimmedStreet = streetAddress?.trim();
  const trimmedCity = city?.trim();
  const trimmedState = state?.trim();
  const trimmedZip = zip?.trim();
  const trimmedCountry = country?.trim();
  const stateZip =
    trimmedState && trimmedZip
      ? `${trimmedState} ${trimmedZip}`
      : trimmedState || trimmedZip;

  return [trimmedStreet, trimmedCity, stateZip, trimmedCountry]
    .filter((part): part is string => Boolean(part))
    .join(", ");
}

/**
 * Detects if an address is a PO Box or similar mailing-only address.
 * This includes various formats like:
 * - P.O. Box, PO Box, P O Box
 * - PO Drawer, P.O. Drawer
 * - Post Office Box
 * - Rural route boxes (Rt 1 Box 123, Route 1 Box 123, RR 1 Box 123)
 * - Standalone Box or Drawer at the start of an address
 *
 * @param address The address string to check
 * @returns true if the address appears to be a PO Box, false otherwise
 */
export function isPOBoxAddress(address?: string | null): boolean {
  if (!address) return false;

  // Convert to uppercase for case-insensitive matching
  const upperAddress = address.toUpperCase().trim();

  // Check for various PO Box patterns
  const poBoxPatterns = [
    // Standard PO Box formats
    /\bP\.?\s*O\.?\s*BOX\b/, // P.O. BOX, PO BOX, P O BOX, P.O BOX
    /\bPOB\s+\d/, // POB followed by numbers

    // Drawer formats
    /\bP\.?\s*O\.?\s*DRAWER\b/, // PO DRAWER, P.O. DRAWER, P O DRAWER

    // Post Office variations
    /\bPOST\s+OFFICE\s+(BOX|DRAWER)\b/,

    // Rural route patterns
    /\b(RT|RTE|ROUTE|RR)\.?\s*\d+\s*BOX\b/, // RT 1 BOX, ROUTE 1 BOX, RR 1 BOX
    /\b(RT|RTE|ROUTE|RR)\.?\s+BOX\s+\d/, // RT BOX 123, ROUTE BOX 123

    // Standalone patterns at the beginning
    /^BOX\s+\d/, // Starts with "BOX" followed by numbers
    /^DRAWER\s+[A-Z0-9]/, // Starts with "DRAWER" followed by letter or number

    // Handle typos like "t 1 Box" (missing R in Rt)
    /\bT\s+\d+\s+BOX\b/, // Common typo for RT
  ];

  // Check if any pattern matches
  return poBoxPatterns.some((pattern) => pattern.test(upperAddress));
}
